import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency, fmtNumber, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Truck, XCircle, Loader2, ShieldAlert, User, Package,
  FileDown, Mail, Share2, FileCog,
} from "lucide-react";

const META_FIELDS = [
  ["consigneeName", "Consignee Name"], ["consigneeAddress", "Consignee Address"],
  ["consigneeGstin", "Consignee GSTIN"], ["buyerPoNumber", "Buyer's Order No."],
  ["buyerPoDate", "Buyer's Order Date"], ["referenceNo", "Reference No."],
  ["referenceDate", "Reference Date"], ["deliveryNote", "Delivery Note"],
  ["dispatchThrough", "Dispatched Through"], ["dispatchDocNo", "Dispatch Doc No."],
  ["destination", "Destination"], ["transporter", "Transporter"],
  ["vehicleNo", "Vehicle No."], ["termsOfDelivery", "Terms of Delivery"],
  ["paymentTerms", "Mode/Terms of Payment"], ["eWayBillNo", "e-Way Bill No."],
];

const num = (v) => (v === "" || v == null ? 0 : Number(v));

function LineStat({ label, value, tone = "text-stone-900" }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">{label}</div>
      <div className={`mt-0.5 font-mono text-sm font-bold ${tone}`}>{value}</div>
    </div>
  );
}

export default function SalesBillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const canSell = ["super_admin", "management", "accounts"].includes(role);
  const canDispatch = ["super_admin", "management", "stores_manager", "store_executive"].includes(role);

  const [bill, setBill] = useState(null);
  const [state, setState] = useState({ loading: true, error: null });
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchQty, setDispatchQty] = useState({});
  const [batchInfo, setBatchInfo] = useState({}); // sku -> {batches, legacyUnbatched}
  const [batchAlloc, setBatchAlloc] = useState({}); // lineId -> { batchNo: qty }
  const [dispatches, setDispatches] = useState([]);
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaForm, setMetaForm] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const canDeleteInvoice = ["super_admin", "accounts", "management"].includes(role);

  const openMeta = () => { setMetaForm({ ...(bill?.invoiceMeta || {}) }); setMetaOpen(true); };
  const saveMeta = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/sales-bills/${id}/invoice-meta`, metaForm);
      setBill(data); setMetaOpen(false);
      toast.success("Invoice details saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not save invoice details")); }
    finally { setBusy(false); }
  };

  const loadDispatches = async () => { try { const { data } = await api.get(`/sales-bills/${id}/dispatches`); setDispatches(Array.isArray(data) ? data : []); } catch { /* ignore */ } };
  const downloadNote = async (d) => {
    try {
      const res = await api.get(`/dispatch/${d.id || d._id}/note`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${d.dispatchNo}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not download dispatch note")); }
  };

  const load = async () => {
    setState({ loading: true, error: null });
    try {
      const { data } = await api.get(`/sales-bills/${id}`);
      setBill(data);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: apiErrorMessage(e, "Could not load sales bill") });
    }
  };
  useEffect(() => { load(); loadDispatches(); /* eslint-disable-line */ }, [id]);

  const b = bill || {};
  const lines = useMemo(() => (Array.isArray(bill?.lines) ? bill.lines : []), [bill]);
  const status = b.status || "DRAFT";
  const isDraft = status === "DRAFT";
  const isConfirmed = status === "CONFIRMED";
  const canDoDispatch = ["CONFIRMED", "PARTIALLY_DISPATCHED"].includes(status);

  const remainingOf = (l) => Math.max(0, +(num(l.orderedQty) - num(l.dispatchedQty)).toFixed(4));

  // FEFO auto-allocate a qty across a sku's available batches (earliest expiry first)
  const fefoAllocate = (batches, qty) => {
    let left = num(qty);
    const out = {};
    for (const b of batches || []) {
      if (left <= 0) break;
      const take = Math.min(left, num(b.available));
      if (take > 0) { out[b.batchNo] = +take.toFixed(4); left = +(left - take).toFixed(4); }
    }
    return out;
  };

  const openDispatch = async () => {
    const init = {};
    lines.forEach((l) => { init[l.id] = String(remainingOf(l)); });
    setDispatchQty(init);
    // fetch batches per distinct sku
    const skus = [...new Set(lines.map((l) => l.sku))];
    const info = {};
    await Promise.all(skus.map(async (sku) => {
      try { const { data } = await api.get(`/finished-goods/${encodeURIComponent(sku)}/batches`); info[sku] = data || { batches: [], legacyUnbatched: 0 }; }
      catch { info[sku] = { batches: [], legacyUnbatched: 0 }; }
    }));
    setBatchInfo(info);
    const alloc = {};
    lines.forEach((l) => { alloc[l.id] = fefoAllocate(info[l.sku]?.batches, remainingOf(l)); });
    setBatchAlloc(alloc);
    setDispatchOpen(true);
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Action failed")); }
    finally { setBusy(false); }
  };

  const downloadInvoice = async () => {
    try {
      const res = await api.get(`/sales-bills/${id}/invoice-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `invoice-${bill?.billNo || id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not generate invoice")); }
  };
  const previewInvoice = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/sales-bills/${id}/invoice-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url); setPreviewOpen(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not render preview")); }
    finally { setBusy(false); }
  };
  const deleteInvoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/sales-bills/${id}`, { params: { reason: deleteReason } });
      toast.success(`Invoice ${data.deleted} deleted`);
      navigate("/sales-bills");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };
  const emailInvoice = async () => {
    const email = window.prompt("Send invoice to email:", bill?.buyerEmail || "");
    if (email === null) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sales-bills/${id}/email`, email ? { email } : {});
      if (data.status === "NOT_CONFIGURED") toast.warning(data.message);
      else if (data.sent) toast.success(`Invoice emailed to ${data.to}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Email failed")); } finally { setBusy(false); }
  };
  const shareWhatsApp = async () => {
    try {
      const { data } = await api.get(`/sales-bills/${id}/share-text`);
      const phone = (data.phone || "").replace(/^0+/, "");
      const base = phone ? `https://wa.me/${phone.length <= 10 ? "91" + phone : phone}` : "https://wa.me/";
      window.open(`${base}?text=${encodeURIComponent(data.text)}`, "_blank");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not prepare share")); }
  };
  const doConfirm = () => { setConfirmOpen(false); act(() => api.post(`/sales-bills/${id}/confirm`, {}), "Sales bill confirmed — FG reserved"); };
  const doCancel = () => { setCancelOpen(false); act(() => api.post(`/sales-bills/${id}/cancel`, {}), "Sales bill cancelled — reservation released"); };
  const doDispatch = () => {
    setDispatchOpen(false);
    const seq = num(b.dispatchSeq) + 1;
    const payloadLines = lines
      .map((l) => {
        const alloc = batchAlloc[l.id] || {};
        const batches = Object.entries(alloc).filter(([, q]) => num(q) > 0).map(([batchNo, q]) => ({ batchNo, qty: num(q) }));
        return { lineId: l.id, qty: num(dispatchQty[l.id]), batches };
      })
      .filter((x) => x.qty > 0);
    if (payloadLines.length === 0) { toast.error("Enter a dispatch quantity"); return; }
    // validate batch allocation does not exceed dispatch qty
    for (const pl of payloadLines) {
      const asum = pl.batches.reduce((s, x) => s + x.qty, 0);
      if (asum - pl.qty > 1e-6) { toast.error("Batch allocation exceeds dispatch quantity for a line"); return; }
    }
    act(() => api.post(`/sales-bills/${id}/dispatch`, { dispatchSeq: seq, lines: payloadLines }), "Dispatched — FG deducted via ledger (batch-traceable)");
  };

  if (state.loading) return <div className="p-6"><LoadingState label="Loading sales bill…" /></div>;
  if (state.error) return <div className="p-6"><ErrorState message={state.error} onRetry={load} /></div>;

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="sales-bill-detail-page">
      <div className="sticky top-0 z-30 border-b border-stone-200 bg-stone-50/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-3.5">
          <div className="flex items-center gap-3">
            <button data-testid="sb-back" onClick={() => navigate("/sales-bills")} className="rounded-lg p-1.5 text-stone-500 hover:bg-stone-200/60 hover:text-stone-800">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-display text-lg font-bold text-stone-900">SALES BILL {b.billNo}</h1>
              <p className="text-xs text-stone-500">{fmtDate(b.billDate)}</p>
            </div>
          </div>
          <StatusBadge value={status} />
        </div>
      </div>

      <div className="mx-auto max-w-5xl space-y-5 px-6 py-6">
        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2" data-testid="sb-actions">
          {isDraft && canSell && (
            <Button data-testid="sb-confirm-btn" size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => setConfirmOpen(true)}>
              <CheckCircle2 className="h-3.5 w-3.5" /> Confirm & Reserve FG
            </Button>
          )}
          {canDoDispatch && canDispatch && (
            <Button data-testid="sb-dispatch-btn" size="sm" className="gap-1.5 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={openDispatch}>
              <Truck className="h-3.5 w-3.5" /> Dispatch
            </Button>
          )}
          {!["DISPATCHED", "CANCELLED"].includes(status) && canSell && (
            <Button data-testid="sb-cancel-btn" size="sm" variant="outline" className="gap-1.5 text-rose-600" disabled={busy} onClick={() => setCancelOpen(true)}>
              <XCircle className="h-3.5 w-3.5" /> Cancel
            </Button>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {canSell && status !== "CANCELLED" && (
              <Button data-testid="sb-invoice-meta-btn" size="sm" variant="outline" className="gap-1.5" onClick={openMeta}>
                <FileCog className="h-3.5 w-3.5" /> Invoice Details
              </Button>
            )}
            <Button data-testid="sb-invoice-pdf-btn" size="sm" variant="outline" className="gap-1.5" onClick={downloadInvoice}>
              <FileDown className="h-3.5 w-3.5" /> Invoice PDF
            </Button>
            <Button data-testid="sb-invoice-preview-btn" size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={previewInvoice}>
              <FileCog className="h-3.5 w-3.5" /> Preview
            </Button>
            <Button data-testid="sb-invoice-email-btn" size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={emailInvoice}>
              <Mail className="h-3.5 w-3.5" /> Email
            </Button>
            <Button data-testid="sb-invoice-whatsapp-btn" size="sm" variant="outline" className="gap-1.5 text-emerald-700" onClick={shareWhatsApp}>
              <Share2 className="h-3.5 w-3.5" /> WhatsApp
            </Button>
            {canDeleteInvoice && (
              <Button data-testid="sb-invoice-delete-btn" size="sm" variant="outline" className="gap-1.5 text-rose-600 hover:text-rose-700" disabled={busy} onClick={() => { setDeleteReason(""); setDeleteOpen(true); }}>
                <XCircle className="h-3.5 w-3.5" /> Delete
              </Button>
            )}
          </div>
          {busy && <Loader2 className="h-4 w-4 animate-spin text-amber-700" />}
        </div>

        {/* Buyer */}
        <div className="flex items-center gap-2.5 rounded-xl border border-stone-200 bg-white px-4 py-3 shadow-sm">
          <User className="h-4 w-4 text-stone-400" />
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Buyer</div>
            <div className="font-mono text-sm font-semibold text-stone-900">{b.buyerId} · {b.buyerName}</div>
            {b.buyerGstin && <div className="text-xs text-stone-400">GSTIN {b.buyerGstin}</div>}
          </div>
        </div>

        {/* Lines */}
        <div className="space-y-3">
          {lines.map((l) => (
            <div key={l.id} data-testid={`sb-line-card-${l.id}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display text-sm font-bold text-stone-900">{l.name}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-stone-500">
                    <span className="font-mono">{l.productId}</span> · {fmtCurrency(l.rate)}/{l.unit}
                    {l.priceSource === "NONE"
                      ? <span className="text-amber-700">· No buyer-specific price configured</span>
                      : <span className="text-emerald-600">· {l.priceSource === "BUYER_PRICE" ? "buyer price" : "manual"}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Line Total</div>
                  <div className="font-mono text-sm font-bold text-stone-900">{fmtCurrency(l.lineTotal)}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-3 rounded-lg bg-stone-50/70 px-4 py-2.5">
                <LineStat label="Ordered" value={`${fmtNumber(l.orderedQty)} ${l.unit}`} />
                <LineStat label="Reserved" value={fmtNumber(l.reservedQty)} tone="text-sky-700" />
                <LineStat label="Dispatched" value={fmtNumber(l.dispatchedQty)} tone="text-emerald-700" />
                <LineStat label="Remaining" value={fmtNumber(remainingOf(l))} tone={remainingOf(l) > 0 ? "text-amber-700" : "text-stone-400"} />
              </div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="flex items-center justify-end gap-6 rounded-xl border border-stone-200 bg-white px-5 py-3 text-sm shadow-sm">
          <span className="text-stone-500">Subtotal <b className="font-mono text-stone-800">{fmtCurrency(b.subtotal)}</b></span>
          <span className="text-stone-500">Discount <b className="font-mono text-stone-800">{fmtCurrency(b.discountTotal)}</b></span>
          <span className="text-stone-500">Tax <b className="font-mono text-stone-800">{fmtCurrency(b.taxTotal)}</b></span>
          <span className="text-stone-800">Grand Total <b data-testid="sb-detail-grand" className="font-mono text-base text-amber-700">{fmtCurrency(b.grandTotal)}</b></span>
        </div>
      </div>

      {/* Confirm dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-emerald-600" /> Confirm sales bill</AlertDialogTitle>
            <AlertDialogDescription>This reserves finished goods for every line. Reservation is blocked if stock is insufficient.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="sb-confirm-yes" className="bg-emerald-600 hover:bg-emerald-700" onClick={doConfirm}>Confirm & Reserve</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {dispatches.length > 0 && (
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="sb-dispatch-notes">
          <div className="mb-2 text-sm font-semibold text-stone-800">Dispatch Notes</div>
          <div className="space-y-1.5">
            {dispatches.map((d) => (
              <div key={d.id || d._id} className="flex items-center justify-between rounded border border-stone-100 px-3 py-1.5 text-sm" data-testid={`sb-dispatch-${d.seq}`}>
                <span className="font-mono text-xs text-stone-600">{d.dispatchNo} · {(d.lines || []).reduce((s, l) => s + Number(l.qty || 0), 0)} units · {(d.createdAt || "").slice(0, 10)}</span>
                <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" data-testid={`sb-note-dl-${d.seq}`} onClick={() => downloadNote(d)}>Download Note</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancel dialog */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this sales bill?</AlertDialogTitle>
            <AlertDialogDescription>Any reserved finished goods will be released back to available stock.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction data-testid="sb-cancel-yes" className="bg-rose-600 hover:bg-rose-700" onClick={doCancel}>Cancel bill</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dispatch dialog */}
      <AlertDialog open={dispatchOpen} onOpenChange={setDispatchOpen}>
        <AlertDialogContent className="bg-white sm:max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-amber-600" /> Dispatch finished goods</AlertDialogTitle>
            <AlertDialogDescription>Enter dispatch quantity per line. Select batches to dispatch (FEFO — earliest expiry — is pre-filled). FG is deducted through the ledger with full batch traceability.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[26rem] space-y-3 overflow-auto">
            {lines.map((l) => {
              const rem = remainingOf(l);
              const info = batchInfo[l.sku] || { batches: [], legacyUnbatched: 0 };
              const alloc = batchAlloc[l.id] || {};
              const allocSum = Object.values(alloc).reduce((s, q) => s + num(q), 0);
              const dq = num(dispatchQty[l.id]);
              const legacyUsed = Math.max(0, +(dq - allocSum).toFixed(4));
              return (
                <div key={l.id} className="rounded-lg border border-stone-200 bg-white p-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-stone-800">{l.name}</div>
                      <div className="text-[11px] text-stone-400">remaining {fmtNumber(rem)} {l.unit}</div>
                    </div>
                    <Input data-testid={`sb-dispatch-qty-${l.id}`} type="number" min="0" max={rem} value={dispatchQty[l.id] ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDispatchQty((q) => ({ ...q, [l.id]: v }));
                        setBatchAlloc((a) => ({ ...a, [l.id]: fefoAllocate(info.batches, v) }));
                      }}
                      className="h-8 w-24 text-right" disabled={rem <= 0} />
                  </div>
                  {(info.batches.length > 0 || info.legacyUnbatched > 0) && dq > 0 && (
                    <div className="mt-2 rounded-md bg-stone-50/70 p-2">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-stone-500">Batch allocation · FEFO recommended</div>
                      <table className="w-full text-xs">
                        <thead className="text-left text-[10px] uppercase text-stone-400">
                          <tr><th className="py-1">Batch</th><th>Mfg</th><th>Expiry</th><th className="text-right">Avail</th><th className="text-right">Allocate</th></tr>
                        </thead>
                        <tbody>
                          {info.batches.map((bt, idx) => (
                            <tr key={bt.batchNo} data-testid={`sb-batch-row-${l.id}-${idx}`}>
                              <td className="py-0.5 font-mono">{bt.batchNo}{idx === 0 && <span className="ml-1 rounded bg-emerald-100 px-1 text-[9px] text-emerald-700">FEFO</span>}</td>
                              <td>{bt.mfgDate || "—"}</td><td>{bt.expiryDate || "—"}</td>
                              <td className="text-right tabular-nums">{fmtNumber(bt.available)}</td>
                              <td className="text-right">
                                <Input type="number" min="0" max={bt.available} data-testid={`sb-batch-alloc-${l.id}-${idx}`}
                                  value={alloc[bt.batchNo] ?? ""}
                                  onChange={(e) => setBatchAlloc((a) => ({ ...a, [l.id]: { ...(a[l.id] || {}), [bt.batchNo]: e.target.value } }))}
                                  className="h-7 w-20 text-right" />
                              </td>
                            </tr>
                          ))}
                          {info.legacyUnbatched > 0 && (
                            <tr className="text-stone-500">
                              <td className="py-0.5 italic" colSpan={3}>Batch not recorded (legacy pool {fmtNumber(info.legacyUnbatched)})</td>
                              <td className="text-right tabular-nums">{fmtNumber(info.legacyUnbatched)}</td>
                              <td className="text-right tabular-nums">{fmtNumber(legacyUsed)}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      <div className={`mt-1 text-[11px] ${allocSum > dq + 1e-6 ? "text-rose-600" : "text-stone-500"}`}>
                        Allocated {fmtNumber(allocSum)} + legacy {fmtNumber(legacyUsed)} of {fmtNumber(dq)}
                        {allocSum > dq + 1e-6 && " · exceeds dispatch qty!"}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="sb-dispatch-yes" className="bg-amber-700 hover:bg-amber-800" onClick={doDispatch}>Dispatch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invoice / dispatch metadata editor (Tally fields) */}
      <Dialog open={metaOpen} onOpenChange={setMetaOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="sb-invoice-meta-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileCog className="h-5 w-5 text-amber-700" /> Invoice / Dispatch Details</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500 -mt-2">Optional Tally fields shown on the invoice PDF. Blank fields are left empty on the print.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[55vh] overflow-y-auto pr-1">
            {META_FIELDS.map(([key, label]) => (
              <div key={key} className={key === "consigneeAddress" ? "sm:col-span-2" : ""}>
                <Label className="text-[11px] text-stone-600">{label}</Label>
                <Input
                  data-testid={`sb-meta-${key}`}
                  value={metaForm[key] || ""}
                  onChange={(e) => setMetaForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMetaOpen(false)}>Cancel</Button>
            <Button data-testid="sb-meta-save" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={saveMeta}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice preview (same PDF as download) */}
      <Dialog open={previewOpen} onOpenChange={(o) => { setPreviewOpen(o); if (!o && previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}>
        <DialogContent className="bg-white max-w-4xl" data-testid="sb-invoice-preview-dialog">
          <DialogHeader><DialogTitle>Invoice Preview · {b.billNo}</DialogTitle>
            <DialogDescription className="text-xs">Same layout and data as the downloadable invoice PDF.</DialogDescription></DialogHeader>
          {previewUrl && <iframe data-testid="sb-invoice-preview-frame" title="Invoice preview" src={previewUrl} className="h-[70vh] w-full rounded border border-stone-200" />}
          <DialogFooter>
            <Button variant="outline" onClick={() => window.open(previewUrl, "_blank")}>Print</Button>
            <Button className="gap-2 bg-amber-700 hover:bg-amber-800" onClick={downloadInvoice}><FileDown className="h-4 w-4" /> Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete invoice (Super Admin / Accounts only) */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-white" data-testid="sb-invoice-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><XCircle className="h-5 w-5 text-rose-600" /> Delete invoice {b.billNo}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the invoice. It is blocked if the invoice has dispatch, receipt allocations, credit notes, returns or reserved stock — use Cancel to void it instead. A deletion audit record (who/when/reason) is always kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1">
            <Label className="text-xs text-stone-600">Reason (recorded)</Label>
            <Input data-testid="sb-delete-reason" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="Why is this being deleted?" className="h-9" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sb-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="sb-delete-confirm" className="gap-2 bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={deleteInvoice}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Delete Invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
